/** Trax serde types. Moves use the bounding rectangle BEFORE placement. */
export type Side = 'First' | 'Second';
export type TraxTile = 'WhiteNS' | 'WhiteEW' | 'WhiteNE' | 'WhiteES' | 'WhiteSW' | 'WhiteWN';
export type TraxSlash = 'Slash' | 'Backslash' | 'Plus';
export type TraxEndKind = 'Loop' | 'Line' | 'MaxPlies';

export interface TraxMove {
  col: number;
  row: number;
  tile: TraxSlash;
}

export interface TraxState {
  width: number;
  height: number;
  cells: (TraxTile | null)[];
  side: Side;
  plies: number;
  /** Direct serde-wasm-bindgen calls use undefined for None; match JSON uses null. */
  outcome?: { winner: Side | null; kind: TraxEndKind } | null;
  /** Current rectangle coordinates; first is chosen, subsequent tiles are forced. */
  last_drop: [number, number][];
}

export const TRAX_TILES: readonly TraxTile[] = [
  'WhiteNS', 'WhiteEW', 'WhiteNE', 'WhiteES', 'WhiteSW', 'WhiteWN',
];
export const TRAX_SHAPES: readonly TraxSlash[] = ['Slash', 'Backslash', 'Plus'];
const WHITE_MASK: Record<TraxTile, number> = {
  WhiteNS: 0b0101, WhiteEW: 0b1010, WhiteNE: 0b0011,
  WhiteES: 0b0110, WhiteSW: 0b1100, WhiteWN: 0b1001,
};

/** 0 = @, 1 = A, 26 = Z, 27 = AA. */
export function encodeTraxColumn(col: number): string {
  if (col === 0) return '@';
  let label = '';
  for (let n = col; n > 0; n = Math.floor((n - 1) / 26)) {
    label = String.fromCharCode(65 + (n - 1) % 26) + label;
  }
  return label;
}

export function traxSymbol(shape: TraxSlash): string {
  return shape === 'Slash' ? '/' : shape === 'Backslash' ? '\\' : '+';
}

export function traxNotation(move: TraxMove): string {
  return `${encodeTraxColumn(move.col)}${move.row}${traxSymbol(move.tile)}`;
}

export function traxCellKey(col: number, row: number): string {
  return `${col}-${row}`;
}

export function traxTileAt(state: TraxState, x: number, y: number): TraxTile | null {
  if (x < 0 || y < 0 || x >= state.width || y >= state.height) return null;
  return state.cells[y * state.width + x] ?? null;
}

export function traxTileShape(tile: TraxTile): TraxSlash {
  if (tile === 'WhiteNS' || tile === 'WhiteEW') return 'Plus';
  return tile === 'WhiteWN' || tile === 'WhiteES' ? 'Slash' : 'Backslash';
}

/**
 * Resolve only the visual orientation of an ENGINE-SUPPLIED legal move.
 * This is not a legality calculator: the engine handles adjacency and forced chains.
 * N=0 E=1 S=2 W=3, matching Rust's ordered ALL_TILES and white_mask.
 */
export function previewTraxTile(state: TraxState, move: TraxMove): TraxTile | null {
  if (state.width === 0 && state.height === 0) {
    if (move.col !== 0 || move.row !== 0) return null;
    return move.tile === 'Slash' ? 'WhiteWN' : move.tile === 'Plus' ? 'WhiteNS' : null;
  }
  const x = move.col - 1;
  const y = move.row - 1;
  let known = 0;
  let white = 0;
  const neighbors = [[x, y - 1], [x + 1, y], [x, y + 1], [x - 1, y]];
  for (let d = 0; d < 4; d++) {
    const neighbor = traxTileAt(state, neighbors[d][0], neighbors[d][1]);
    if (neighbor === null) continue;
    known |= 1 << d;
    if (WHITE_MASK[neighbor] & (1 << ((d + 2) % 4))) white |= 1 << d;
  }
  if (known === 0 || traxTileAt(state, x, y) !== null) return null;
  return TRAX_TILES.find((tile) =>
    traxTileShape(tile) === move.tile && ((WHITE_MASK[tile] ^ white) & known) === 0,
  ) ?? null;
}

export function groupTraxMoves(moves: readonly TraxMove[]): Map<string, TraxMove[]> {
  const result = new Map<string, TraxMove[]>();
  for (const move of moves) {
    const key = traxCellKey(move.col, move.row);
    const group = result.get(key) ?? [];
    if (!group.some((existing) => existing.tile === move.tile)) group.push(move);
    result.set(key, group);
  }
  return result;
}

export function traxOutcomeLabel(outcome: NonNullable<TraxState['outcome']>): string {
  if (outcome.winner === null) return '引き分け（200手上限）';
  const color = outcome.winner === 'First' ? '白' : '赤';
  return `${color}の勝ち（${outcome.kind === 'Loop' ? 'ループ完成' : '8列または8行以上の線完成'}）`;
}
